import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Print as PrintIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearAllIcon
} from '@mui/icons-material';
import { getLocations } from '../services/locationService';
import { getWarehouses } from '../services/warehouseService';

const BarcodePrintDialog = ({ open, onClose }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedLocations, setSelectedLocations] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch warehouses on component mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const warehousesData = await getWarehouses();
        setWarehouses(warehousesData);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        setError('Failed to load warehouses');
      }
    };

    if (open) {
      fetchWarehouses();
    }
  }, [open]);

  // Fetch locations when warehouse is selected
  useEffect(() => {
    const fetchLocations = async () => {
      if (!selectedWarehouse) {
        setLocations([]);
        setSelectedLocations(new Set());
        return;
      }

      try {
        setLoading(true);
        const locationsData = await getLocations({ warehouse_id: selectedWarehouse });

        // Sort locations by rack and shelf
        const sortedLocations = locationsData.sort((a, b) => {
          // Sort by rack first
          const aRack = isNaN(a.rack) ? a.rack : parseInt(a.rack);
          const bRack = isNaN(b.rack) ? b.rack : parseInt(b.rack);

          if (typeof aRack === 'number' && typeof bRack === 'number') {
            if (aRack !== bRack) return aRack - bRack;
          } else {
            const rackCompare = a.rack.localeCompare(b.rack);
            if (rackCompare !== 0) return rackCompare;
          }

          // Then sort by shelf
          const aShelf = isNaN(a.shelf) ? a.shelf : parseInt(a.shelf);
          const bShelf = isNaN(b.shelf) ? b.shelf : parseInt(b.shelf);

          if (typeof aShelf === 'number' && typeof bShelf === 'number') {
            return aShelf - bShelf;
          }
          return a.shelf.localeCompare(b.shelf);
        });

        setLocations(sortedLocations);
        setSelectedLocations(new Set());
        setLoading(false);
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError('Failed to load locations');
        setLoading(false);
      }
    };

    fetchLocations();
  }, [selectedWarehouse]);

  // Handle warehouse selection
  const handleWarehouseChange = (event) => {
    setSelectedWarehouse(event.target.value);
    setError(null);
  };

  // Handle location selection
  const handleLocationToggle = (locationId) => {
    const newSelected = new Set(selectedLocations);
    if (newSelected.has(locationId)) {
      newSelected.delete(locationId);
    } else {
      newSelected.add(locationId);
    }
    setSelectedLocations(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    setSelectedLocations(new Set(locations.map(loc => loc.id)));
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedLocations(new Set());
  };

  // Handle print
  const handlePrint = () => {
    if (selectedLocations.size === 0) {
      setError('Please select at least one location to print');
      return;
    }

    // Generate barcodes for selected locations
    const selectedLocationData = locations.filter(loc => selectedLocations.has(loc.id));

    // Create print content
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent(selectedLocationData);

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Generate print content with 3 barcodes per 4x6" label
  const generatePrintContent = (locationData) => {
    const chunks = [];
    for (let i = 0; i < locationData.length; i += 3) {
      chunks.push(locationData.slice(i, i + 3));
    }

    const barcodeHtml = chunks.map((chunk, pageIndex) => {
      const barcodeItems = chunk.map((location, index) => {
        const canvasId = `barcode-${pageIndex}-${index}`;
        return `
          <div class="barcode-item">
            <canvas id="${canvasId}"></canvas>
            <div class="location-text">
              ${location.warehouse?.name || 'Unknown'} - Rack ${location.rack} - Shelf ${location.shelf}
            </div>
          </div>
        `;
      }).join('');

      return `
        <div class="label-page">
          ${barcodeItems}
        </div>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Location Barcodes</title>
          <style>
            @page {
              size: 4in 6in;
              margin: 0.1in;
            }

            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }

            .label-page {
              width: 3.8in;
              height: 5.8in;
              display: flex;
              flex-direction: column;
              justify-content: space-around;
              align-items: center;
              page-break-after: always;
              border: 1px dashed #ccc;
            }

            .label-page:last-child {
              page-break-after: avoid;
            }

            .barcode-item {
              text-align: center;
              margin: 0.1in 0;
              width: 100%;
            }

            .barcode-item canvas {
              max-width: 3.6in;
              height: auto;
            }

            .location-text {
              font-size: 10px;
              font-weight: bold;
              margin-top: 2px;
              word-wrap: break-word;
            }

            @media print {
              .label-page {
                border: none;
              }
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
        </head>
        <body>
          ${barcodeHtml}
          <script>
            window.onload = function() {
              ${chunks.map((chunk, pageIndex) =>
                chunk.map((location, index) =>
                  `JsBarcode("#barcode-${pageIndex}-${index}", "${location.barcode}", {
                    format: "CODE128",
                    width: 2,
                    height: 60,
                    displayValue: true,
                    fontSize: 12,
                    margin: 5
                  });`
                ).join('\n')
              ).join('\n')}
            };
          </script>
        </body>
      </html>
    `;
  };

  // Reset state when dialog closes
  const handleClose = () => {
    setSelectedWarehouse('');
    setLocations([]);
    setSelectedLocations(new Set());
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PrintIcon sx={{ mr: 1 }} />
          Print Location Barcodes
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Warehouse Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="warehouse-select-label">Select Warehouse</InputLabel>
            <Select
              labelId="warehouse-select-label"
              value={selectedWarehouse}
              label="Select Warehouse"
              onChange={handleWarehouseChange}
            >
              {warehouses.map(warehouse => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} ({warehouse.location})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Location Selection */}
        {selectedWarehouse && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Select Locations ({selectedLocations.size} selected)
              </Typography>
              <Box>
                <Button
                  size="small"
                  startIcon={<SelectAllIcon />}
                  onClick={handleSelectAll}
                  disabled={loading}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  startIcon={<ClearAllIcon />}
                  onClick={handleDeselectAll}
                  disabled={loading}
                  sx={{ ml: 1 }}
                >
                  Deselect All
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper sx={{ maxHeight: 400, overflow: 'auto', p: 2 }}>
                <Grid container spacing={1}>
                  {locations.map(location => (
                    <Grid item xs={12} sm={6} md={4} key={location.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedLocations.has(location.id)}
                            onChange={() => handleLocationToggle(location.id)}
                          />
                        }
                        label={`Rack ${location.rack} - Shelf ${location.shelf}`}
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={selectedLocations.size === 0 || loading}
        >
          Print Barcodes ({selectedLocations.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodePrintDialog;
